"use client";

import { bbox } from "@turf/turf";
import { Geometry } from "geojson";
import { LngLatBoundsLike } from "maplibre-gl";
import { useMapLibre } from "@/hooks";
import type { Observatory } from "@/types";

function useObservatory(observatory?: Observatory) {
  const _observatory = observatory;

  const { flyTo, fitBounds } = useMapLibre();

  const getGeometry = (observatory?: Observatory) => {
    const data = observatory || _observatory;
    if (data) return data.stat?.boundingBox as Geometry;
  };

  const getBounds = (observatory?: Observatory) => {
    const data = observatory || _observatory;
    const geometry = getGeometry(data);

    if (geometry?.type === "Polygon") {
      return bbox(geometry) as LngLatBoundsLike;
    }
  };

  const getPosition = (observatory?: Observatory) => {
    const data = observatory || _observatory;
    const geometry = getGeometry(data);

    if (geometry?.type === "Point") {
      const coords = geometry.coordinates;
      return { lng: coords[0], lat: coords[1] };
    }
  };

  const locate = (observatory?: Observatory) => {
    const data = observatory || _observatory;
    const geometry = getGeometry(data);

    if (!geometry?.type) {
      return false;
    }

    switch (geometry.type) {
      case "Point": {
        const position = getPosition(data);
        if (position) {
          flyTo(position);
          return true;
        }
        break;
      }
      case "Polygon": {
        const bounds = getBounds(data);
        if (bounds) {
          fitBounds(bounds);
          return true;
        }
        break;
      }
    }
    return false;
  };

  const checkObservatoryByTagIds = (
    tagIds: number[],
    observatory?: Observatory,
  ): boolean => {
    const data = observatory || _observatory;
    if (data) {
      return data.tags?.some((tag) => tagIds.includes(tag.tagId)) ?? false;
    }
    return false;
  };

  return {
    getBounds,
    getPosition,
    locate,
    checkObservatoryByTagIds,
  };
}

export { useObservatory };
